import _ from 'lodash';
import React, { useState } from 'react';
import { Formik } from 'formik';
import onClickOutside from 'react-onclickoutside';
import { Grid, PrimaryButton, Row, TertiaryButton } from '@mlc/web-ui-toolkit';
import { timesCircleSolid } from '@mlc/symbols';
import * as Yup from 'yup';
import { SearchFilterValues } from '../../../../logic/domains/searchFilters.domain';
import {
	addFormEvent,
	EVENT_FORM_ABANDONED,
	EVENT_FORM_COMPLETED,
} from '../../../../logic/services/digital-data.service';
import { getInvestmentOptions } from '../../../../logic/functions/investmentOptions.function';
import { useAdviserDetails } from '../../../hooks/AppStateHook';
import { FilterElements } from '../FilterElements';
import AccountFilters from '../AccountFilters/AccountFilters';
import InvestmentFilters, {
	FundOption,
} from '../InvestmentFilters/InvestmentFilters';
import * as Styled from './styled';

interface Props {
	handleFilterToggle?: any;
	onFiltersUpdate: Function;
	onSearchSubmit: Function;
	onReset: Function;
	initialValues: SearchFilterValues;
}

const ADVANCED_SEARCH_FILTERS_FORM = 'advanced-search-filters-form';

export const defaultFilterValues: SearchFilterValues = {
	accountType: ['all'],
	ageMin: '',
	ageMax: '',
	postcode: '',
	advisedPortfolioMin: '',
	advisedPortfolioMax: '',
	investmentType: ['all'],
	investmentOptions: [],
	showLinkedAccountsOnly: false,
	includeClosedAccounts: false,
};

//Yup based validation schema to go here
const filterSchema = Yup.object().shape({
	ageMin: Yup.number()
		.typeError('Age must be a number')
		.when('ageMax', {
			is: val => val !== undefined,
			then: Yup.number()
				.typeError('Age must be a number')
				.max(Yup.ref('ageMax'), 'Min age is greater than max age'),
		}),
	ageMax: Yup.number().typeError('Age must be a number'),

	advisedPortfolioMin: Yup.number()
		.typeError('Amount must be a number')
		.when('advisedPortfolioMax', {
			is: val => val !== undefined,
			then: Yup.number()
				.typeError('Amount must be a number')
				.max(
					Yup.ref('advisedPortfolioMax'),
					'Min amount is greater than max amount'
				),
		}),
	advisedPortfolioMax: Yup.number().typeError('Amount must be a number'),
});

export const SearchFilterModal = (props: Props) => {
	const { customerToken } = useAdviserDetails();
	const [investmentOptionNames, setInvestmentOptionNames] = useState<
		Array<FundOption>
	>([]);

	React.useEffect(() => {
		getInvestmentOptions(customerToken).then(options => {
			const data = _(options)
				.sort()
				.map(option => ({ value: option, label: option } as FundOption))
				.value();
			setInvestmentOptionNames(data);
		});
	}, []);
	function handleApplyFilters(values: SearchFilterValues) {
		props.onSearchSubmit(values);
		addFormEvent(EVENT_FORM_COMPLETED, ADVANCED_SEARCH_FILTERS_FORM);
	}

	function focusFirstError() {
		let errors: any = document.getElementsByClassName(
			FilterElements.validationError
		);
		if (errors && errors.length > 0) {
			errors[0].focus();
		}
	}

	function retainFocus(event: any) {
		const applyFiltersBtn = document.getElementById(
			`${FilterElements.applyFiltersBtnId}`
		);
		const closeFilterBtn = document.getElementById(
			`${FilterElements.closeFiltersBtnId}`
		);

		if (event.keyCode === 9) {
			if (event.shiftKey && document.activeElement === closeFilterBtn) {
				// @ts-ignore
				applyFiltersBtn.focus();
				event.preventDefault();
			} else if (
				!event.shiftKey &&
				document.activeElement === applyFiltersBtn
			) {
				// @ts-ignore
				closeFilterBtn.focus();
				event.preventDefault();
			}
		}
	}

	/*
        Prevent default for accessibility:
        "When the filter is closed by any method ensure the focus is sent back to the "Search filters" button." - PHX-771
    */
	// @ts-ignore
	SearchFilterModal.handleClickOutside = (event: any) => {
		event.preventDefault();
		props.handleFilterToggle();
	};

	return (
		<Styled.FiltersContainer
			data-id={FilterElements.filtersContainerId}
			boxShadow="mlcShadowStrong"
			border="mlcBorderThin"
			borderColor="mlcMediumGrey"
		>
			<Formik
				initialValues={props.initialValues}
				enableReinitialize={false}
				validationSchema={filterSchema}
				onSubmit={handleApplyFilters}
				validate={(values: any) => {
					props.onFiltersUpdate(values);
				}}
				render={formikProps => {
					const { values, handleSubmit, resetForm } = formikProps;

					if (!_.isEqual(props.initialValues, values)) {
						props.onFiltersUpdate(values);
					}

					return (
						<form
							noValidate
							onSubmit={handleSubmit}
							id={ADVANCED_SEARCH_FILTERS_FORM}
						>
							<Styled.FormContentWrapper>
								<Styled.CloseButton
									colorTheme="mlcText"
									onClick={props.handleFilterToggle}
									onKeyDown={retainFocus}
									aria-label="Close filters menu"
									data-id={FilterElements.closeFiltersBtnId}
									id={FilterElements.closeFiltersBtnId}
								>
									Close <Styled.CloseIcon symbol={timesCircleSolid} />
								</Styled.CloseButton>
								<Grid>
									<AccountFilters
										accountFilters={{
											accountType: values.accountType,
											ageMin: values.ageMin,
											ageMax: values.ageMax,
											postcode: values.postcode,
											includeClosedAccounts: values.includeClosedAccounts,
											showLinkedAccountsOnly: values.showLinkedAccountsOnly,
										}}
										formikProps={formikProps}
									/>

									<Row>
										<Styled.Divider />
									</Row>

									<InvestmentFilters
										investmentFilters={{
											advisedPortfolioMin: values.advisedPortfolioMin,
											advisedPortfolioMax: values.advisedPortfolioMax,
											investmentType: values.investmentType,
											investmentOptions: values.investmentOptions,
											initialInvestmentOptions: investmentOptionNames,
										}}
										formikProps={formikProps}
									/>
								</Grid>
							</Styled.FormContentWrapper>
							<Styled.ButtonGroupContainer>
								<Styled.ClearApplyButtonGroup>
									<TertiaryButton
										type="reset"
										onClick={() => {
											addFormEvent(
												EVENT_FORM_ABANDONED,
												ADVANCED_SEARCH_FILTERS_FORM
											);
											props.onReset();
											resetForm({
												values: {
													...defaultFilterValues,
												} as any,
											});
										}}
										data-id={FilterElements.clearFiltersBtnId}
									>
										Clear
									</TertiaryButton>
									<PrimaryButton
										id={FilterElements.applyFiltersBtnId}
										type="submit"
										data-id={FilterElements.applyFiltersBtnId}
										onClick={focusFirstError}
										onKeyDown={retainFocus}
									>
										Apply
									</PrimaryButton>
								</Styled.ClearApplyButtonGroup>
							</Styled.ButtonGroupContainer>
						</form>
					);
				}}
			/>
		</Styled.FiltersContainer>
	);
};

const clickOutsideConfig = {
	// @ts-ignore
	handleClickOutside: () => SearchFilterModal.handleClickOutside,
};

export default onClickOutside(SearchFilterModal, clickOutsideConfig);
